// WorkFlowEditor.tsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Stack,
    TextField,
    Select,
    Menu,
    MenuItem,
    Button,
    Paper,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
} from "@mui/material";
import {
    typeData,
    appActivity,
    FormData,
    templateData,
    Approver,
    ApprovalType,
    Reporter,
    RejectionType,
    MailTemplateData,
    ToUser,
    MailRecipientTargets
} from "../../data/WorkFlowEditorType";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import PreviewIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import ActivityEditor from "./componets/ActivityEditor";
import CustomFormPreview from "./componets/CustomFormPreview";
import FormItemEditor from "./componets/FormItemEditor";

interface Props {
    workflow: { type: typeData; name: string; description: string };
    selectTypeList: typeData[];
    selectReporterList: Reporter[];
    selectToUserList: ToUser[];
    activities: appActivity[];
    formItems: FormData[];
    setFormItems: (updated: FormData[]) => void;
    templateList: templateData[];
    mailTemplateList: MailTemplateData[];
    approverList: Approver[];
    reporterList: Reporter[];
    approvalTypeOptions: { value: ApprovalType; label: string }[];
    rejectionTypeOptions: { value: RejectionType; label: string }[];
    setWorkflow: (wf: { type: typeData; name: string; description: string }) => void;
    handleChangeType: (newType: typeData) => void;
    setActivities: (updated: appActivity[]) => void;
    handleDelete: (id: string) => void;
    handleChangeName: (id: string, value: string) => void;
    handleChangeTemplate: (id: string, value: string) => void;
    handleChangeMailTemplate: (id: string, value: string) => void;
    handleChangeMailFormat: (id: string, subject: string, body: string, toUsers: ToUser[]) => void;
    handleChangeReporter: (id: string, reporterIds: string[]) => void;
    handleChangeApprovalCondition: (id: string, value: string) => void;
    handleChangeRejectionCondition: (id: string, value: string) => void;
    handleChangePercentage: (id: string, percent: number, isApproval: boolean) => void;
    handleChangeConditionalApprover: (id: string, approverIds: string[], isApproval: boolean) => void;
    handleSaveEditData: () => void;
    handleTempSave: () => void;
    handleReuseWorkflow: (mode: "full" | "formOnly") => void;
    handleChangeMailRecipientTargets: (id: string, targets: MailRecipientTargets) => void;
    validationErrors?: ValidationErrors;
    isNewWorkflow?: boolean;
}

/**
 * フォーム全体のバリデーションエラー構造を表すインターフェース
 */
interface ValidationErrors {
    name?: string;
    type?: string;
    activitiesEmpty?: boolean;
    activities?: {
        [key: string]: {
            name?: string;
            template?: string;
            mailSetting?: string;
        };
    };
    formItemsEmpty?: boolean;
    formItems?: {
        [key: string]: {
            label?: string;
            type?: string;
            defaultValue?: string;
            selectItems?: { [key: string]: string };
        }
    };
}

/**
 * ワークフローエディターコンポーネント
 * - 左側に基本情報・フォームアイテム・アクティビティの編集エリアを表示する
 * - 右側にフォームのプレビューエリアを表示する
 * - 保存・一時保存・キャンセル操作と入力バリデーションを管理する
 * - props: workflow（基本情報）, activities（アクティビティ一覧）, formItems（フォームアイテム一覧）ほか
 */
const WorkFlowEditor: React.FC<Props> = ({
    workflow,
    selectReporterList,
    selectTypeList,
    selectToUserList,
    activities,
    formItems,
    templateList,
    mailTemplateList,
    approverList,
    approvalTypeOptions,
    rejectionTypeOptions,
    setFormItems,
    setWorkflow,
    setActivities,
    handleChangeType,
    handleDelete,
    handleChangeName,
    handleChangeTemplate,
    handleChangeMailTemplate,
    handleChangeMailFormat,
    handleReuseWorkflow,
    handleChangeReporter,
    handleChangeApprovalCondition,
    handleChangeRejectionCondition,
    handleChangePercentage,
    handleChangeConditionalApprover,
    handleSaveEditData,
    handleTempSave,
    handleChangeMailRecipientTargets,
    validationErrors: propValidationErrors,
    isNewWorkflow
}) => {
    const navigate = useNavigate();
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showTempSaveDialog, setShowTempSaveDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(propValidationErrors || {});
    const [showGlobalError, setShowGlobalError] = useState(false);
    const editAreaRef = useRef<HTMLDivElement>(null);

    const [reuseAnchorEl, setReuseAnchorEl] = useState<null | HTMLElement>(null);
    const openReuseMenu = Boolean(reuseAnchorEl);

    // 複製メニューを開く
    const handleOpenReuseMenu = (event: React.MouseEvent<HTMLElement>) => {
        setReuseAnchorEl(event.currentTarget);
    };

    // 複製メニューを閉じる
    const handleCloseReuseMenu = () => {
        setReuseAnchorEl(null);
    };

    /**
     * フォーム全体のバリデーションを実行する
     * - 申請種別・申請書名・アクティビティ・フォームアイテムの必須入力を検証する
     * - エラーがある場合はページ最上部にスクロールしてエラー表示を行う
     * @returns バリデーション成功時はtrue、エラーがある場合はfalse
     */
    const validateForm = (): boolean => {
        const errors: ValidationErrors = {};
        let hasErrors = false;

        // 申請種別のバリデーション
        if (!workflow.type?.id || workflow.type.id === "") {
            errors.type = "※申請種別を選択してください";
            hasErrors = true;
        }

        // 申請書名のバリデーション
        if (!workflow.name || workflow.name.trim() === "") {
            errors.name = "※申請書名を入力してください";
            hasErrors = true;
        }

        // アクティビティのバリデーション
        if (!activities || activities.length === 0) {
            errors.activitiesEmpty = true;
            hasErrors = true;
        } else if (activities && activities.length > 0) {
            errors.activities = {};
            activities.forEach(activity => {
                const activityErrors: {
                    name?: string;
                    template?: string;
                    mailSetting?: string;
                } = {};

                // アクティビティ名のバリデーション
                if (!activity.appActivityName || activity.appActivityName.trim() === "") {
                    activityErrors.name = "※アクティビティ名を入力してください";
                    hasErrors = true;
                }

                // テンプレートのバリデーション
                if (!activity.template?.id || activity.template.id === "") {
                    activityErrors.template = "※テンプレートを選択してください";
                    hasErrors = true;
                }

                // メールフォーマット設定のバリデーション
                if (
                    (!activity.mailTemplate?.id || activity.mailTemplate.id === "") &&
                    (!activity.mailFormat?.subject ||
                        activity.mailFormat.subject === "" ||
                        !activity.mailFormat.body ||
                        activity.mailFormat.body === "")
                ) {
                    activityErrors.mailSetting = "※メールフォーマットを設定してください";
                }

                if (
                    activityErrors.name ||
                    activityErrors.template ||
                    activityErrors.mailSetting
                ) {
                    errors.activities![activity.applicationActivityId] = activityErrors;
                }
            });

            // アクティビティエラーがない場合、activitiesプロパティを削除
            if (Object.keys(errors.activities).length === 0) {
                delete errors.activities;
            }
        }

        // フォームアイテムのバリデーション
        if (!formItems || formItems.length === 0) {
            errors.formItemsEmpty = true;
            hasErrors = true;
        } else if (formItems && formItems.length > 0) {
            errors.formItems = {};
            formItems.forEach(formItem => {
                const formItemErrors: {
                    label?: string;
                    type?: string;
                    defaultValue?: string;
                    selectItems?: { [key: string]: string };
                } = {};

                // ラベルのバリデーション
                if (!formItem.label || formItem.label.trim() === "") {
                    formItemErrors.label = "※ラベルを入力してください";
                    hasErrors = true;
                }

                // タイプのバリデーション
                if (!formItem.type || formItem.type.trim() === "") {
                    formItemErrors.type = "※タイプを選択してください";
                    hasErrors = true;
                }

                // 金額のバリデーション
                if (formItem.type === "amount") {
                    const msg = validateAmountDefaultValue(formItem.defaultValue);
                    if (msg) {
                        formItemErrors.defaultValue = msg;
                        hasErrors = true;
                    }
                }

                // selectタイプのselectItemのバリデーション
                if (formItem.type === "select" && formItem.selectItemList && formItem.selectItemList.length > 0) {
                    const selectItemErrors: { [key: string]: string } = {};
                    formItem.selectItemList.forEach(selectItem => {
                        if (!selectItem.label || selectItem.label.trim() === "") {
                            selectItemErrors[selectItem.id] = "※選択肢名を入力してください";
                            hasErrors = true;
                        }
                    });
                    if (Object.keys(selectItemErrors).length > 0) {
                        formItemErrors.selectItems = selectItemErrors;
                    }
                }

                if (formItemErrors.label || formItemErrors.type ||
                     formItemErrors.defaultValue || formItemErrors.selectItems) {
                    errors.formItems![formItem.formId] = formItemErrors;
                }
            });

            // フォームアイテムエラーがない場合、formItemsプロパティを削除
            if (Object.keys(errors.formItems).length === 0) {
                delete errors.formItems;
            }
        }

        setValidationErrors(errors);

        if (hasErrors) {
            setShowGlobalError(true);
            // ページの最上部と編集エリアの最上部にスクロール
            setTimeout(() => {
                // ページを最上部にスクロール
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // 編集エリアを最上部にスクロール
                if (editAreaRef.current) {
                    editAreaRef.current.scrollTop = 0;
                }
            }, 100);
        } else {
            setShowGlobalError(false);
        }

        return !hasErrors;
    };

    // 特定フィールドのバリデーションエラーをクリア
    const clearFieldError = (fieldPath: string) => {
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (fieldPath === 'activitiesEmpty') {
                delete newErrors.activitiesEmpty;
            } else if (fieldPath === 'formItemsEmpty') {
                delete newErrors.formItemsEmpty;
            } else if (fieldPath.startsWith('activities.')) {
                const [, activityId, field] = fieldPath.split('.');
                if (newErrors.activities?.[activityId]?.[field as keyof typeof newErrors.activities[typeof activityId]]) {
                    newErrors.activities = { ...newErrors.activities };
                    newErrors.activities[activityId] = { ...newErrors.activities[activityId] };
                    delete (newErrors.activities[activityId] as any)[field];

                    // そのアクティビティに他のエラーがない場合、そのアクティビティのエラーオブジェクトを削除
                    if (Object.keys(newErrors.activities[activityId]).length === 0) {
                        delete newErrors.activities[activityId];
                    }

                    // アクティビティエラーがない場合、activitiesプロパティを削除
                    if (Object.keys(newErrors.activities).length === 0) {
                        delete newErrors.activities;
                    }
                }
            } else if (fieldPath.startsWith('formItems.')) {
                const pathParts = fieldPath.split('.');
                const formItemId = pathParts[1];
                const field = pathParts[2];

                if (field === 'selectItems' && pathParts.length === 4) {
                    // 特定のselectItemのエラークリアを処理: formItems.{formItemId}.selectItems.{selectItemId}
                    const selectItemId = pathParts[3];
                    if (newErrors.formItems?.[formItemId]?.selectItems?.[selectItemId]) {
                        newErrors.formItems = { ...newErrors.formItems };
                        newErrors.formItems[formItemId] = { ...newErrors.formItems[formItemId] };
                        newErrors.formItems[formItemId].selectItems = { ...newErrors.formItems[formItemId].selectItems };
                        delete newErrors.formItems[formItemId].selectItems![selectItemId];

                        // selectItemsに他のエラーがない場合、selectItemsフィールドを削除
                        if (Object.keys(newErrors.formItems[formItemId].selectItems!).length === 0) {
                            delete newErrors.formItems[formItemId].selectItems;
                        }
                    }
                } else if (newErrors.formItems?.[formItemId]?.[field as keyof typeof newErrors.formItems[typeof formItemId]]) {
                    newErrors.formItems = { ...newErrors.formItems };
                    newErrors.formItems[formItemId] = { ...newErrors.formItems[formItemId] };
                    delete (newErrors.formItems[formItemId] as any)[field];
                }

                // そのフォームアイテムに他のエラーがない場合、そのフォームアイテムのエラーオブジェクトを削除
                if (newErrors.formItems?.[formItemId] && Object.keys(newErrors.formItems[formItemId]).length === 0) {
                    delete newErrors.formItems[formItemId];
                }

                // フォームアイテムエラーがない場合、formItemsプロパティを削除
                if (newErrors.formItems && Object.keys(newErrors.formItems).length === 0) {
                    delete newErrors.formItems;
                }
            } else {
                delete (newErrors as any)[fieldPath];
            }

            // まだエラーがあるかチェックし、なければグローバルエラー表示を非表示にする
            const hasAnyErrors = Object.keys(newErrors).length > 0 ||
                (newErrors.activities && Object.keys(newErrors.activities).length > 0) ||
                (newErrors.formItems && Object.keys(newErrors.formItems).length > 0);
            if (!hasAnyErrors) {
                setShowGlobalError(false);
            }

            return newErrors;
        });
    };

    // 空はOK、0以上の整数文字列のみOK
    const AMOUNT_REGEX = /^(?:0|[1-9][0-9]*)$/;

    const validateAmountDefaultValue = (raw: unknown): string | undefined => {
        const v = raw == null ? "" : String(raw);

        // 「未設定」はOK（空文字）
        if (v === "") return undefined;

        // 空白のみはNG（""はOKだが "  " はNG）
        if (v.trim() === "") {
            return "金額のデフォルト値は 0以上の整数で入力してください（カンマ不可）";
        }

        // 数字列のみ（0 or 先頭ゼロなし）を許可
        if (!AMOUNT_REGEX.test(v)) {
            return "金額のデフォルト値は 0以上の整数で入力してください（カンマ不可）";
        }

        return undefined;
    };

    return (
        <Box sx={{ flex: "flex", flexDirection: "column" }}>
            <Box display="flex" >

                {/* 左側の編集エリア */}
                <Box ref={editAreaRef} display="flex" flexDirection="column" width="50%" p={2} gap={2} sx={{ maxHeight: "1000px", overflow: "auto" }}>

                    {/* グローバルエラー表示 */}
                    {showGlobalError && (
                        <Alert severity="error">
                            必須項目が未入力です。再度ご確認ください。
                        </Alert>
                    )}
                    {/* 基本情報 */}
                    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'GrayText' }}>
                                基本情報
                            </Typography>
                            <EditIcon sx={{ fontSize: 20, color: 'GrayText' }} />
                        </Box>
                        <Paper sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <FormControl fullWidth error={!!validationErrors.type}>
                                    <InputLabel id="type-select-label">申請種別</InputLabel>
                                    <Select
                                        labelId="type-select-label"
                                        value={workflow.type?.id || ""}
                                        onChange={(e) => {
                                            const selected = selectTypeList.find(t => t.id === e.target.value);
                                            if (selected) {
                                                handleChangeType(selected);
                                                clearFieldError('type');
                                            }
                                        }}
                                        label="申請種別"
                                        error={!!validationErrors.type}
                                    >
                                        {selectTypeList.map((type) => (
                                            <MenuItem key={type.id} value={type.id}>
                                                {type.typeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {validationErrors.type && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, fontSize: '1rem' }}>
                                            {validationErrors.type}
                                        </Typography>
                                    )}
                                </FormControl>
                                <TextField
                                    label="申請書名"
                                    fullWidth
                                    value={workflow.name}
                                    error={!!validationErrors.name}
                                    helperText={validationErrors.name}
                                    sx={{
                                        '& .MuiFormHelperText-root': {
                                            fontSize: '1rem'
                                        }
                                    }}
                                    onChange={(e) => {
                                        setWorkflow({ ...workflow, name: e.target.value });
                                        if (validationErrors.name && e.target.value.trim()) {
                                            clearFieldError('name');
                                        }
                                    }}
                                />
                                <TextField
                                    label="詳細"
                                    fullWidth
                                    value={workflow.description}
                                    onChange={(e) => setWorkflow({ ...workflow, description: e.target.value })}
                                />
                            </Stack>
                        </Paper>
                    </Box>

                    {/* FormItemEditor（申請書項目編集） */}
                    <Box
                        border={validationErrors.formItemsEmpty ? 2 : 1}
                        borderColor={validationErrors.formItemsEmpty ? "#d32f2f" : "grey.300"}
                        borderRadius={1}
                        p={2}
                        sx={{ position: 'relative' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'GrayText' }}>
                                フォームアイテム
                            </Typography>
                            <EditIcon sx={{ fontSize: 15, color: 'GrayText', ml: 0.5 }} />
                        </Box>
                        {validationErrors.formItemsEmpty && (
                            <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
                                <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                フォームアイテムを追加してください
                            </Typography>
                        )}
                        <FormItemEditor
                            formItems={formItems}
                            setFormItems={setFormItems}
                            validationErrors={validationErrors.formItems}
                            clearFieldError={clearFieldError}
                        />
                    </Box>

                    {/*　ActivityEditor（アクティビティ設定）*/}
                    <ActivityEditor
                        activities={activities}
                        setActivities={setActivities}
                        templateList={templateList}
                        mailTemplateList={mailTemplateList}
                        approverList={approverList}
                        selectReporterList={selectReporterList}
                        selectToUserList={selectToUserList}
                        approvalTypeOptions={approvalTypeOptions}
                        rejectionTypeOptions={rejectionTypeOptions}
                        handleChangeName={(id: string, value: string) => {
                            handleChangeName(id, value);
                            if (validationErrors.activities?.[id]?.name && value.trim()) {
                                clearFieldError(`activities.${id}.name`);
                            }
                        }}
                        handleChangeTemplate={(id: string, value: string) => {
                            handleChangeTemplate(id, value);
                            if (validationErrors.activities?.[id]?.template && value) {
                                clearFieldError(`activities.${id}.template`);
                            }
                        }}
                        handleChangeMailTemplate={handleChangeMailTemplate}
                        handleChangeMailFormat={handleChangeMailFormat}
                        handleChangeReporter={handleChangeReporter}
                        handleChangeApprovalCondition={handleChangeApprovalCondition}
                        handleChangeRejectionCondition={handleChangeRejectionCondition}
                        handleChangePercentage={handleChangePercentage}
                        handleChangeConditionalApprover={handleChangeConditionalApprover}
                        handleDelete={handleDelete}
                        handleChangeMailRecipientTargets={handleChangeMailRecipientTargets}
                        validationErrors={validationErrors.activities}
                        emptyError={validationErrors.activitiesEmpty}
                        clearFieldError={clearFieldError}
                    />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "1000px", width: "50%", overflow: "auto" }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2, pt: 2 }}>
                        {!isNewWorkflow && (
                            <Button
                                variant="outlined"
                                onClick={handleOpenReuseMenu}
                                sx={{
                                    color: 'primary',
                                }}
                            >
                                このワークフローをコピーして新規作成 ▼
                            </Button>
                        )}
                        <Menu
                            anchorEl={reuseAnchorEl}
                            open={openReuseMenu}
                            onClose={handleCloseReuseMenu}
                            PaperProps={{
                                sx: {
                                    minWidth: "21.2%",
                                    mt: 0,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: 'grey.200',

                                }
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleReuseWorkflow("full");
                                    handleCloseReuseMenu();
                                }}
                                sx={{
                                    px: 2,
                                    pb: 0.5,
                                    color: "primary.main",
                                    '&:hover': {
                                        backgroundColor: 'primary.50'
                                    }
                                }}
                            >
                                ワークフロー全体をコピーする
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleReuseWorkflow("formOnly");
                                    handleCloseReuseMenu();
                                }}
                                sx={{
                                    px: 2,
                                    pt: 0.5,
                                    color: "primary.main",
                                    '&:hover': {
                                        backgroundColor: 'primary.50'
                                    }
                                }}
                            >
                                申請書情報のみコピーする
                            </MenuItem>
                        </Menu>
                    </Box>

                    {/* プレビュー符号 */}
                    <Box sx={{ mx: 2, mt: 2 }}>
                        <Chip icon={<PreviewIcon />} label="プレビュー" color="primary" />
                    </Box>
                    {/* 右側のプレビューエリア */}
                    <CustomFormPreview
                        type={workflow.type.typeName}
                        name={workflow.name}
                        description={workflow.description}
                        formItems={formItems}
                        appActivityList={activities}
                    />
                </Box>
            </Box>
            {/* 操作ボタン */}
            <Box display="flex" justifyContent="flex-end" margin={1} gap={2}>
                <Button variant="outlined" color="inherit" onClick={() => setShowCancelDialog(true)}>キャンセル</Button>
                <Button variant="outlined" color="primary" onClick={() => {
                    if (validateForm()) {
                        setShowTempSaveDialog(true);
                    }
                }}>一時保存</Button>
                <Button variant="contained" onClick={() => {
                    if (validateForm()) {
                        setShowSaveDialog(true);
                    }
                }}>保存</Button>
            </Box>
            {/* 警告ダイアログ：保存・キャンセル */}
            <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)}>
                <DialogTitle>保存確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        編集内容を保存しますか？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSaveDialog(false)}>キャンセル</Button>
                    <Button
                        onClick={() => {
                            handleSaveEditData();
                            setShowSaveDialog(false);
                        }}
                        color="primary"
                    >
                        保存
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showTempSaveDialog} onClose={() => setShowTempSaveDialog(false)}>
                <DialogTitle>一時保存確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        編集内容を一時保存しますか？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTempSaveDialog(false)}>キャンセル</Button>
                    <Button
                        onClick={() => {
                            handleTempSave();
                            setShowTempSaveDialog(false);
                        }}
                        color="primary"
                    >
                        一時保存
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
                <DialogTitle>キャンセル確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        入力内容は保存されていません。入力を破棄しますか?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCancelDialog(false)}>戻る</Button>
                    <Button
                        onClick={() => {
                            navigate("/WorkFlowPage"); // 一覧画面に戻る
                            setShowCancelDialog(false);
                        }}
                        color="error"
                    >
                        破棄
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkFlowEditor;
